import pluralize from 'pluralize';
import { DeviceReportLocal, DomainClaimMatchingTeam } from '@/store';

type DomainClaimRestriction = DeviceReportLocal['domainClaimRestriction'];

/**
 * Legacy single-line message — still used by the lightweight surfaces (account-picker
 * tooltips, checkout snackbars, UserAccount nudge). The rich dialog uses
 * getDomainClaimDialogContent below.
 */
export function getDomainClaimRestrictionMessage (
	restriction: DomainClaimRestriction,
	teamDisplayLabel: string,
): string {
	if (!restriction) return '';
	return `This email address you use to sign in is associated with the domain ${restriction.normalizedDomain}, which is owned by the company ${restriction.companyName}. The Personal account or ${teamDisplayLabel} account you are trying to access is outside the management of that company, so usage is restricted here.`;
}

// --- Domain Claim Resolution Dialog -----------------------------------------

// Axis A: which kind of account the Caller is currently on.
export type DomainClaimAccountState =
	| 'PERSONAL_FREE'
	| 'PERSONAL_PAID'       // paid Personal membership, email is self-service changeable
	| 'PERSONAL_PAID_SCIM'  // paid Personal membership, email locked by SCIM provisioning
	| 'TEAM_WRONG';         // on a Team whose Company doesn't own the domain

// Every action the dialog can offer. The component maps kind -> handler.
export type DomainClaimCtaKind =
	| 'switch'        // switch to the recommended Company team (primary when a match exists)
	| 'recheck'       // re-resolve membership (primary when no match exists yet)
	| 'changeEmail'
	| 'managePlan'
	| 'downgrade'
	| 'seeAll'        // open the full account picker
	| 'signInOther'
	| 'dismiss';

export interface DomainClaimCta {
	kind: DomainClaimCtaKind;
	label: string;
	primary: boolean;
}

export interface DomainClaimDialogContent {
	accountState: DomainClaimAccountState;
	hasMatchingTeam: boolean;
	recommendedTeam: DomainClaimMatchingTeam | null;
	title: string;
	bodyHtml: string;
	ctas: DomainClaimCta[];
}

export interface DomainClaimDialogInputs {
	restriction: DomainClaimRestriction;
	teamDisplayLabel: string;
	ownerSelection: 'USER' | 'TEAM';
	isPaid: boolean;   // store getter showStripeUserMembershipPortalBtn
	isScim: boolean;   // isScimProvisionedUser
}

export function resolveDomainClaimAccountState (
	{ ownerSelection, isPaid, isScim }: Pick<DomainClaimDialogInputs, 'ownerSelection' | 'isPaid' | 'isScim'>,
): DomainClaimAccountState {
	if (ownerSelection === 'TEAM') return 'TEAM_WRONG';
	if (isPaid && isScim) return 'PERSONAL_PAID_SCIM';
	if (isPaid) return 'PERSONAL_PAID';
	return 'PERSONAL_FREE';
}

/**
 * Builds the full content for the Domain Claim Resolution Dialog: a distinct
 * headline/body per account state (Axis A), and a primary CTA chosen by whether the
 * Caller has a matching Company team (Axis B) — Switch vs Recheck.
 */
export function getDomainClaimDialogContent (
	inputs: DomainClaimDialogInputs,
): DomainClaimDialogContent | null {
	const { restriction, teamDisplayLabel } = inputs;
	if (!restriction) return null;

	const domain: string = restriction.normalizedDomain;
	const company: string = restriction.companyName;
	const team: string = teamDisplayLabel;
	const teamPlural: string = pluralize(team);
	const matchingTeams: DomainClaimMatchingTeam[] = restriction.matchingTeams || [];
	const recommendedTeam: DomainClaimMatchingTeam | null = matchingTeams[0] || null;
	const hasMatchingTeam: boolean = !!recommendedTeam;
	const accountState: DomainClaimAccountState = resolveDomainClaimAccountState(inputs);

	// Primary CTA: Switch when there's a Company team to go to, otherwise Recheck.
	const primaryCta: DomainClaimCta = hasMatchingTeam
		? { kind: 'switch', label: `Switch to ${recommendedTeam.teamName}`, primary: true }
		: { kind: 'recheck', label: `Reload ${teamPlural}`, primary: true };

	// Account-state-specific secondary escape hatches.
	const seeAllCta: DomainClaimCta[] = hasMatchingTeam
		? [{ kind: 'seeAll', label: 'See all accounts', primary: false }]
		: [{ kind: 'signInOther', label: 'Sign in with another email', primary: false }];
	const dismissCta: DomainClaimCta = { kind: 'dismiss', label: 'Dismiss', primary: false };

	let secondaryCtas: DomainClaimCta[];
	switch (accountState) {
		case 'PERSONAL_PAID':
			secondaryCtas = [
				{ kind: 'changeEmail', label: 'Change email', primary: false },
				{ kind: 'managePlan', label: 'Manage plan', primary: false },
				...seeAllCta,
			];
			break;
		case 'PERSONAL_PAID_SCIM':
			secondaryCtas = [
				{ kind: 'downgrade', label: 'Downgrade to free', primary: false },
				...seeAllCta,
			];
			break;
		case 'TEAM_WRONG':
		case 'PERSONAL_FREE':
		default:
			secondaryCtas = [...seeAllCta];
			break;
	}

	// When there's no Company team to switch to, lead with the invite ask regardless of
	// account state; paid users keep their downgrade/change-email hatches as secondaries.
	if (!hasMatchingTeam) {
		const inviteBody: string = `<strong>${domain}</strong> is managed by <strong>${company}</strong>, and you're not in one of their ${teamPlural} yet. Ask a ${company} admin to invite you, then reload below.`;
		// Paid Personal accounts still carry a (now-paused) membership — fold in the same
		// downgrade guidance the switch-available paid states give.
		let paidClause: string = '';
		if (accountState === 'PERSONAL_PAID') {
			paidClause = ` Your paid Personal membership is paused — change your email to a personal address to keep it, or downgrade to free.`;
		} else if (accountState === 'PERSONAL_PAID_SCIM') {
			paidClause = ` Your paid Personal membership is paused and this email can't be changed, so we recommend downgrading to free.`;
		}
		return {
			accountState,
			hasMatchingTeam,
			recommendedTeam,
			title: `Ask ${company} to add you`,
			bodyHtml: `${inviteBody}${paidClause}`,
			ctas: [primaryCta, ...secondaryCtas, dismissCta],
		};
	}

	const titlesByState: Record<DomainClaimAccountState, string> = {
		PERSONAL_FREE: `Use your ${company} account here`,
		PERSONAL_PAID: `Your membership is on a managed-domain email`,
		PERSONAL_PAID_SCIM: `Switch to your ${company} ${team}`,
		TEAM_WRONG: `This ${team} isn't part of ${company}`,
	};
	const bodiesByState: Record<DomainClaimAccountState, string> = {
		PERSONAL_FREE: `<strong>${domain}</strong> is managed by <strong>${company}</strong>. Switch to a ${company} ${team} to get working.`,
		PERSONAL_PAID: `<strong>${domain}</strong> is managed by <strong>${company}</strong>, so paid features are paused on this Personal account. Switch to your ${company} ${team} to keep working. To keep your membership, change your sign-in email to a personal address — otherwise we recommend downgrading to free.`,
		PERSONAL_PAID_SCIM: `Your account is provisioned by <strong>${company}</strong>, so this email can't be changed and paid features are paused on Personal. Switch to your ${company} ${team} to keep working, then downgrade this Personal account to free.`,
		TEAM_WRONG: `<strong>${domain}</strong> is managed by <strong>${company}</strong>, so usage is reserved for their ${teamPlural}. Switch to your ${company} ${team} to continue.`,
	};

	return {
		accountState,
		hasMatchingTeam,
		recommendedTeam,
		title: titlesByState[accountState],
		bodyHtml: bodiesByState[accountState],
		ctas: [primaryCta, ...secondaryCtas, dismissCta],
	};
}
